import { useMemo, useEffect, useCallback } from 'react';
import { DomainOrderResponseDto } from '@tiendanube/common';
import { DomainLifecycleStatus } from '@tiendanube/common/src/domains/nuvem-domains/types';
import { useNavegate } from 'App/hooks';
import { configurationsRoutes } from 'domains/Configurations';
import useTranslationConfigurations from 'domains/Configurations/useTranslationConfigurations';
import { useStoreInfo } from 'domains/PartnersApps/hooks';
import configuringImage from './img/en-configuración.png';
import emailVerificationImage from './img/pago-confirmado.png';
import { useEmailResend } from './useEmailResend';
import {
  useAcknowledgeDomainOrderMutation,
  useGetDomainLifecycleDetailsQuery,
} from '../../../../domainsApi';
import { pendingVerificationStates } from '../../utils';

const POLLING_INTERVAL = 10000;

type SuccessCardView = 'emailVerification' | 'configuring';

interface HeroData {
  title: string;
  description: string;
  image: string;
  imageAlt: string;
  buttonText: string;
  buttonAction: () => void;
  buttonDisabled: boolean;
  showCooldown: boolean;
}

interface UseSuccessOrderCardLogicParams {
  order: DomainOrderResponseDto;
}

export const useSuccessOrderCardLogic = ({
  order,
}: UseSuccessOrderCardLogicParams) => {
  const t = useTranslationConfigurations('domains.orderDetails.successOrder');
  const { id: storeId } = useStoreInfo();
  const { goTo } = useNavegate();

  const { handleResendEmail, cooldownSeconds, canResend } =
    useEmailResend(storeId);

  const [acknowledgeOrder, { isLoading: isAcknowledging }] =
    useAcknowledgeDomainOrderMutation();

  const { data: domainDetails, refetch } = useGetDomainLifecycleDetailsQuery(
    order.domainName,
    {
      skip: !order.domainName,
      pollingInterval: 0,
    },
  );

  const view = useMemo<SuccessCardView>(() => {
    if (
      !domainDetails ||
      pendingVerificationStates.includes(domainDetails.lifecycleStatus)
    ) {
      return 'emailVerification';
    }

    if (domainDetails.lifecycleStatus === DomainLifecycleStatus.ACTIVE) {
      return 'configuring';
    }

    return 'emailVerification';
  }, [domainDetails]);

  useEffect(() => {
    if (view !== 'emailVerification') return;

    let timeoutId: NodeJS.Timeout;
    let isCancelled = false;

    const poll = async () => {
      if (isCancelled) return;

      try {
        const result = await refetch();

        const shouldContinuePolling =
          !isCancelled &&
          result.data &&
          pendingVerificationStates.includes(result.data.lifecycleStatus);

        if (shouldContinuePolling) {
          timeoutId = setTimeout(poll, POLLING_INTERVAL);
        }
      } catch (error) {
        if (!isCancelled) {
          timeoutId = setTimeout(poll, POLLING_INTERVAL);
        }
      }
    };

    timeoutId = setTimeout(poll, POLLING_INTERVAL);

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [view, refetch]);

  const handleAcknowledge = useCallback(async () => {
    try {
      await acknowledgeOrder(order.payOrderId).unwrap();
      goTo(configurationsRoutes.domains);
    } catch {
      // Error is handled by RTK Query
    }
  }, [acknowledgeOrder, order.payOrderId, goTo]);

  const heroData = useMemo<HeroData>(() => {
    const isConfiguring = view === 'configuring';

    if (isConfiguring) {
      return {
        title: t('configuringTitle'),
        description: t('configuringDescription', { domain: order.domainName }),
        image: configuringImage,
        imageAlt: t('configuringImageAlt'),
        buttonText: t('acknowledgeButton'),
        buttonAction: handleAcknowledge,
        buttonDisabled: isAcknowledging,
        showCooldown: false,
      };
    }

    return {
      title: t('title'),
      description: t('description', { email: order.contact?.email || '' }),
      image: emailVerificationImage,
      imageAlt: t('imageAlt'),
      buttonText: t('resendButton'),
      buttonAction: handleResendEmail,
      buttonDisabled: !canResend,
      showCooldown: cooldownSeconds > 0,
    };
  }, [
    view,
    order.domainName,
    order.contact?.email,
    t,
    handleResendEmail,
    handleAcknowledge,
    canResend,
    cooldownSeconds,
    isAcknowledging,
  ]);
  return {
    heroData,
    isConfiguring: view === 'configuring',
    cooldownSeconds,
  };
};
